/**
 * WorkTabs · 工作页签状态（domain 层）
 * ------------------------------------------------------------
 * 顶部标签栏的数据层：任何视图打开即成为标签（对话/讨论群/异常/
 * 任务/架构/通讯录……），可点击切换、可关闭；「聊天」为固定首页
 * 标签不可关闭；标签数有上限，超出自动挤掉最早的「非固定且非激活」
 * 标签（激活签是用户随时要切回来的锚点，绝不淘汰）。
 *
 * 放在 domain 层的原因：页签是跨层共享的纯状态（无 DOM）——shell
 * （router/页签条/左栏）与 features（应用页叶子导航）都要写入，
 * 归 domain 可避免 features→shell 的反向依赖。
 *
 * 与路由的关系：router.afterEach 统一 openTab——无论从左栏、底栏、
 * 全局搜索还是页签自身进入，标签栏始终同步可用。
 */
import { reactive } from 'vue'

export interface WorkTab { id: string; path: string; title: string; pinned: boolean }

const MAX_TABS = 12
let seq = 1

/** 路径 → 页面标题（router.meta.title 优先） */
export const PAGE_TITLES: Record<string, string> = {
  '/chat': '聊天',
  '/messenger': '讨论群',
  '/arch': '架构',
  '/product/overview': '产研总览',
  '/product/goals': '项目目标',
  '/product/plan': '项目计划',
  '/product/solutions': '解决方案',
  '/product/requirements/detail': '需求详情',
  '/fde': 'FDE 交付',
  '/apps': '应用',
  '/tasks': '任务',
  '/calendar': '日程',
  '/mail': '邮件',
  '/cloud-space': '空间',
  '/cloud-pc': '云电脑',
  '/profile': '我的',
  '/procurement': '采购管理',
  '/components': '组件库',
  '/sys/users': '用户管理',
  '/sys/accounts': '账号管理',
  '/sys/roles': '角色管理',
  '/sys/perms': '权限管理',
  '/sys/tenants': '租户管理',
  '/tenant/profile': '基本信息维护',
  '/sys/audit': '审计日志',
  '/sys/notices': '公告管理',
  '/product/requirements': '需求清单',
  '/product/features': '功能清单',
  '/product/test-cases': '测试用例'
}

/** 架构浏览器视角 → 标签名（与 ArchBrowser.VIEWS 对齐）：同一路由 /arch 不同 view 各成一签时，
 *  标签名按视角区分，避免多个「架构」签无法分辨。view 值为 4A 缩写（BA/DA/AA/TA）+ overview + 三工程阶段 */
const ARCH_VIEW_TITLES: Record<string, string> = {
  overview: '架构总览',
  BA: '业务架构',
  DA: '数据架构',
  AA: '应用架构',
  TA: '技术架构',
  product: '产品设计',
  dev: '系统开发',
  ops: '运营维护',
  spec: '原则规范'
}

/** 工作台 /chat 内部视图 → 标签名（与 RailNav.wvGo 对齐） */
const CHAT_VIEW_TITLES: Record<string, string> = {
  chat: '聊天',
  groups: '讨论群'
}

/** 架构浏览器语义路径视角段 → 视角名（与 features/architecture/semanticRoutes.ts 的 ARCH_PATHS 对齐） */
const ARCH_VIEW_PATH_TITLES: Record<string, string> = {
  overview: '架构总览', ba: '业务架构', aa: '应用架构', da: '数据架构', ta: '技术架构',
  product: '产品设计', development: '开发测试', operations: '运营维护', standards: '原则规范'
}

/** 架构浏览器语义路径子标签段 → 标签名：页面深入到哪级子标签，工作页签就显示哪级名称。
 *  与各视图页签按钮文案及 semanticRoutes.ts 的 ARCH_PATHS.tabs 对齐；对象详情页（第 4 段为对象编码）
 *  在应用内经 setTabTitle 以对象名覆盖，刷新直达时回落到所属子标签名。 */
const ARCH_TAB_PATH_TITLES: Record<string, Record<string, string>> = {
  product: { components: '标准组件库', templates: '典型页面模板', patterns: '设计模式指引', menus: '菜单清单', functions: '功能清单', configuration: '配置项清单' },
  ba: { 'value-streams': '价值流', capabilities: '业务能力', processes: '业务流程', scenarios: '端到端场景', rules: '业务规则', validations: '业务校验', goals: '目标与指标', roles: '业务角色', 'state-machines': '状态机', events: '业务事件' },
  da: { concepts: '概念清单', 'business-objects': '业务对象', 'reference-codes': '参考代码', entities: '逻辑实体', 'data-flows': '数据流向' },
  ta: { network: '网络架构', deployments: '部署架构', software: '软件架构', 'implementation-mappings': '实现映射' },
  development: { apis: 'API 目录', functions: '函数清单', tables: '物理表清单', events: '事件清单', operations: '操作清单', 'test-cases': '测试用例' },
  operations: { issues: '问题处理机制', emergencies: '异常应急预案', backups: '备份恢复机制' },
  aa: { apps: '应用目录', services: '服务与数据职责' },
  overview: { blueprint: '产研蓝图', relations: '架构关系', metadata: '元数据', 'navigation-bindings': '导航绑定', governance: '治理' }
}

const state = reactive<{ tabs: WorkTab[]; activeId: string }>({
  tabs: [{ id: 't0', path: '/chat', title: PAGE_TITLES['/chat'], pinned: true }],
  activeId: ''
})

export function titleFor(fullPath: string, metaTitle?: string): string {
  const base = fullPath.split('?')[0]
  /* 优先级：菜单叶子登记的覆盖标题 > 路由 meta.title > 路径/query 推导 > 路径默认标题 */
  const override = TITLE_OVR.get(fullPath)
  if (override) return override
  /* 架构语义路径（/arch/<视角>/<子标签>/…）：子标签名优先于路由 meta 的视角名，
     页面深入到哪级，页签就显示哪级 */
  const seg = base.split('/').filter(Boolean)
  if (seg[0] === 'arch' && seg[1]) {
    const tabTitle = seg[2] ? ARCH_TAB_PATH_TITLES[seg[1]]?.[seg[2]] : ''
    if (tabTitle) return tabTitle
    const viewTitle = ARCH_VIEW_PATH_TITLES[seg[1]]
    if (viewTitle) return viewTitle
  }
  if (metaTitle) return metaTitle
  /* 单路由多视图页面：按 query 中的视角/视图命名，使同一路由不同子页面各成可分辨的签 */
  const q = new URLSearchParams(fullPath.slice(base.length))
  if (base === '/arch') {
    const view = q.get('view')
    if (view && ARCH_VIEW_TITLES[view]) return ARCH_VIEW_TITLES[view]
  } else if (base === '/chat') {
    const view = q.get('view')
    if (view && CHAT_VIEW_TITLES[view]) return CHAT_VIEW_TITLES[view]
  }
  return PAGE_TITLES[base] || '工作视图'
}

/** 页签标题覆盖（fullPath → 标题）：菜单叶子跳转前登记，让同路径不同视图的页签按功能命名
 *  （如 /proc?view=req →「采购申请」）；对应标签关闭/被淘汰时一并清理，不常驻累积 */
const TITLE_OVR = new Map<string, string>()
export function setTabTitle(fullPath: string, title: string) { TITLE_OVR.set(fullPath, title) }
function forgetTitle(path: string) { TITLE_OVR.delete(path) }

/** 打开/激活标签（afterEach 唯一入口）：以 fullPath 为唯一标识——同路径不同 query（/proc?view=req 与 view=ord）各自成签，点过的菜单页可快速切换定位；重复点击同一目标仅激活不重复开签。
 *  标签名即点击项名称：不追加序号、不做运行时消歧。命名唯一性由数据源保证
 *  （每个菜单项/视角名称唯一），同一路径只会激活已有签不会重复开签。 */
export function openTab(fullPath: string, title: string) {
  let tab = state.tabs.find(t => t.path === fullPath)
  if (!tab) {
    tab = { id: `t${seq++}`, path: fullPath, title, pinned: false }
    state.tabs.push(tab)
    if (state.tabs.length > MAX_TABS) {
      /* 淘汰最早的「非固定且非激活」签；找不到可淘汰对象时允许暂时超限（无害） */
      const idx = state.tabs.findIndex(t => !t.pinned && t.id !== tab!.id && t.id !== state.activeId)
      if (idx >= 0) { forgetTitle(state.tabs[idx].path); state.tabs.splice(idx, 1) }
    }
  } else if (tab.title !== title) {
    tab.title = title
  }
  state.activeId = tab.id
}

/** 关闭标签；若关闭的是当前签，返回应导航到的相邻路径，否则返回 null */
export function closeTab(id: string): string | null {
  const idx = state.tabs.findIndex(t => t.id === id)
  if (idx < 0) return null
  const tab = state.tabs[idx]
  if (tab.pinned) return null
  state.tabs.splice(idx, 1)
  forgetTitle(tab.path)
  if (state.activeId === id) {
    const next = state.tabs[Math.min(idx, state.tabs.length - 1)]
    state.activeId = next.id
    return next.path
  }
  return null
}

/** 关闭其他标签：保留当前激活标签与固定标签（如「聊天」首页签），其余不分左右全部关闭，返回关闭数量；激活签不动故无需导航 */
export function closeOthers(): number {
  const before = state.tabs.length
  const kept: WorkTab[] = []
  for (const t of state.tabs) {
    if (t.pinned || t.id === state.activeId) kept.push(t)
    else forgetTitle(t.path)
  }
  state.tabs.splice(0, state.tabs.length, ...kept)
  return before - kept.length
}

export function useWorkTabs() {
  return state
}
